import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Download, Upload, RotateCcw, Trash2, Plus,
    Database, CheckCircle2, AlertTriangle, Clock, X, HardDrive,
} from "lucide-react";
import { toast } from "sonner";
import { api, uploadBackup, BASE } from "../../services/api";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("sk-SK", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function ColBadge({ name }) {
    const colors = {
        referencie: "bg-blue-500/15 text-blue-300",
        technika:   "bg-purple-500/15 text-purple-300",
        sluzby:     "bg-cyan-500/15 text-cyan-300",
        testimonials:"bg-green-500/15 text-green-300",
        bookings:   "bg-amber-500/15 text-amber-300",
        admins:     "bg-red-500/15 text-red-300",
        contacts:   "bg-pink-500/15 text-pink-300",
    };
    return (
        <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full ${colors[name] || "bg-white/8 text-zinc-400"}`}>
            {name}
        </span>
    );
}

// ── Restore confirm modal ─────────────────────────────────────────────────────
function RestoreModal({ backup, onClose, onConfirm }) {
    const [drop, setDrop] = useState(false);
    const [running, setRunning] = useState(false);

    const confirm = async () => {
        setRunning(true);
        await onConfirm(backup.name, drop);
        setRunning(false);
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }} transition={{ type: "spring", damping: 28, stiffness: 300 }}
                className="relative w-full max-w-md bg-nova-surface border border-white/10 rounded-2xl overflow-hidden"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <h2 className="font-heading font-black text-white">Obnoviť zálohu</h2>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="bg-nova-black/40 rounded-xl p-4 border border-white/8">
                        <div className="font-mono text-xs text-zinc-400">{backup.name}</div>
                        <div className="text-white text-sm mt-1">{fmtDate(backup.created_at)}</div>
                        <div className="text-zinc-500 text-xs mt-0.5">{backup.total_docs} dokumentov · {backup.uploads} súborov</div>
                    </div>

                    <label className="flex items-start gap-3 p-3 rounded-xl border border-white/8 cursor-pointer hover:border-white/20 transition-colors">
                        <input
                            type="checkbox"
                            checked={drop}
                            onChange={e => setDrop(e.target.checked)}
                            className="mt-0.5 accent-nova-cyan"
                        />
                        <div>
                            <div className="text-white text-sm font-medium">Zmazať existujúce dáta pred obnovením</div>
                            <div className="text-zinc-500 text-xs mt-0.5">
                                Ak nezaškrtnuté, existujúce záznamy sa aktualizujú (bezpečnejšie). Ak zaškrtnuté, kolekcie sa najprv vyprázdnia.
                            </div>
                        </div>
                    </label>

                    {drop && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            Všetky aktuálne dáta budú nenávratne zmazané.
                        </div>
                    )}
                </div>

                <div className="px-6 pb-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white text-sm transition-colors">
                        Zrušiť
                    </button>
                    <button onClick={confirm} disabled={running}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400 disabled:opacity-50 transition-colors">
                        <RotateCcw className="w-4 h-4" />
                        {running ? "Obnovovanie…" : "Obnoviť"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ── Upload zone ───────────────────────────────────────────────────────────────
function UploadZone({ onUploaded }) {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const handle = async (file) => {
        if (!file || !file.name.endsWith(".zip")) {
            toast.error("Očakáva sa .zip súbor");
            return;
        }
        setUploading(true);
        try {
            const result = await uploadBackup(file);
            toast.success(`Záloha nahraná: ${result.name}`);
            onUploaded(result);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files[0]); }}
            className={`flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                dragging ? "border-nova-cyan bg-nova-cyan/8" : "border-white/15 hover:border-white/30 hover:bg-white/3"
            }`}
        >
            <input ref={inputRef} type="file" accept=".zip" className="hidden"
                onChange={e => handle(e.target.files?.[0])} />
            <Upload className={`w-8 h-8 ${dragging ? "text-nova-cyan" : "text-zinc-600"}`} />
            <div className="text-center">
                <div className="text-white text-sm font-medium">
                    {uploading ? "Nahrávam…" : "Nahrať zálohu (.zip)"}
                </div>
                <div className="text-zinc-500 text-xs mt-0.5">Drag & drop alebo kliknite</div>
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminBackup() {
    const [backups, setBackups]       = useState([]);
    const [loading, setLoading]       = useState(true);
    const [creating, setCreating]     = useState(false);
    const [restoring, setRestoring]   = useState(null);
    const [deleting, setDeleting]     = useState(null);
    const [expanded, setExpanded]     = useState(null);
    const [downloading, setDownloading] = useState(null); // { name, progress 0-100 | -1 = indeterminate }

    const load = useCallback(async () => {
        setLoading(true);
        try { setBackups(await api.listBackups()); }
        catch { toast.error("Nepodarilo sa načítať zálohy"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            const result = await api.createBackup();
            toast.success(`Záloha vytvorená — ${result.total_docs} dokumentov`);
            await load();
        } catch (e) {
            toast.error(e.message || "Chyba pri zálohe");
        } finally {
            setCreating(false);
        }
    };

    const handleRestore = async (name, drop) => {
        try {
            const result = await api.restoreBackup(name, drop);
            const total = Object.values(result.restored).reduce((s, r) => s + r.total, 0);
            toast.success(`Obnovené — ${total} dokumentov, ${result.uploads} súborov`);
            setRestoring(null);
        } catch (e) {
            toast.error(e.message || "Chyba pri obnovovaní");
        }
    };

    const handleDelete = async (name) => {
        if (!window.confirm(`Zmazať zálohu „${name}"?`)) return;
        setDeleting(name);
        try {
            await api.deleteBackup(name);
            setBackups(prev => prev.filter(b => b.name !== name));
            toast.success("Záloha zmazaná");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setDeleting(null);
        }
    };

    const handleDownload = async (name) => {
        const t   = localStorage.getItem("kb_admin_token");
        const url = api.downloadBackupUrl(name);
        setDownloading({ name, progress: 0 });
        try {
            const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
            if (!res.ok) throw new Error();
            const total  = parseInt(res.headers.get("Content-Length") || "0");
            const reader = res.body.getReader();
            const chunks = [];
            let received = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                setDownloading({ name, progress: total ? Math.round((received / total) * 100) : -1 });
            }
            const blob = new Blob(chunks);
            const burl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = burl;
            a.setAttribute("download", `${name}.zip`);
            a.click();
            URL.revokeObjectURL(burl);
        } catch {
            toast.error("Stiahnutie zlyhalo");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Zálohovanie</h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Záloha obsahuje všetky kolekcie databázy aj uploadnuté súbory.
                    </p>
                </div>
                <button
                    onClick={handleCreate}
                    disabled={creating}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-60 transition-colors"
                >
                    {creating ? (
                        <><span className="w-4 h-4 border-2 border-nova-black/30 border-t-nova-black rounded-full animate-spin" /> Zálohujem…</>
                    ) : (
                        <><Plus className="w-4 h-4" /> Vytvoriť zálohu</>
                    )}
                </button>
            </div>

            {/* Upload zone */}
            <div className="mb-8">
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Import zálohy</div>
                <UploadZone onUploaded={() => load()} />
            </div>

            {/* Backups list */}
            <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
                    Existujúce zálohy ({backups.length})
                </div>

                {loading ? (
                    <div className="space-y-3 animate-pulse">
                        {[1, 2, 3].map(n => <div key={n} className="h-20 bg-nova-surface/40 rounded-2xl" />)}
                    </div>
                ) : backups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
                        <Database className="w-10 h-10 mb-3 opacity-30" />
                        <div className="text-sm">Žiadne zálohy. Vytvorte prvú zálohu.</div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {backups.map(b => (
                            <motion.div
                                key={b.name}
                                layout
                                className="rounded-2xl border border-white/8 bg-nova-surface/40 overflow-hidden"
                            >
                                {/* Row */}
                                <div className="flex items-center gap-4 px-5 py-4">
                                    <div className="w-10 h-10 rounded-xl bg-nova-black/40 border border-white/8 flex items-center justify-center shrink-0">
                                        <HardDrive className="w-4 h-4 text-zinc-500" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs text-white font-medium truncate">{b.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5 text-[10px] font-mono text-zinc-500">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {fmtDate(b.created_at)}
                                            </span>
                                            <span>·</span>
                                            <span>{b.total_docs} dok.</span>
                                            <span>·</span>
                                            <span>{b.uploads} súb.</span>
                                        </div>
                                    </div>

                                    {/* Collection badges — show on wider screens */}
                                    <div className="hidden lg:flex flex-wrap gap-1 max-w-xs">
                                        {(b.collections || []).map(c => <ColBadge key={c} name={c} />)}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            onClick={() => setRestoring(b)}
                                            title="Obnoviť"
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-400 border border-amber-500/20 hover:bg-amber-500/10 transition-colors"
                                        >
                                            <RotateCcw className="w-3.5 h-3.5" /> Obnoviť
                                        </button>
                                        <button
                                            onClick={() => handleDownload(b.name)}
                                            disabled={downloading?.name === b.name}
                                            title="Stiahnuť"
                                            className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/8 transition-colors disabled:opacity-40"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setExpanded(expanded === b.name ? null : b.name)}
                                            className="p-2 rounded-lg text-zinc-600 hover:text-zinc-300 transition-colors lg:hidden"
                                        >
                                            <Database className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(b.name)}
                                            disabled={deleting === b.name}
                                            className="p-2 rounded-lg text-zinc-700 hover:text-red-400 transition-colors disabled:opacity-40"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Download progress bar */}
                                <AnimatePresence>
                                    {downloading?.name === b.name && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden border-t border-white/5"
                                        >
                                            <div className="px-5 py-3 flex items-center gap-3">
                                                <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                                                    {downloading.progress >= 0 ? `${downloading.progress} %` : "Sťahujem…"}
                                                </span>
                                                <div className="flex-1 h-1 bg-white/8 rounded-full overflow-hidden">
                                                    {downloading.progress >= 0 ? (
                                                        <motion.div
                                                            className="h-full bg-nova-cyan rounded-full"
                                                            initial={{ width: 0 }}
                                                            animate={{ width: `${downloading.progress}%` }}
                                                            transition={{ ease: "linear", duration: 0.1 }}
                                                        />
                                                    ) : (
                                                        <motion.div
                                                            className="h-full w-1/3 bg-nova-cyan rounded-full"
                                                            animate={{ x: ["−100%", "400%"] }}
                                                            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Expanded collections (mobile) */}
                                <AnimatePresence>
                                    {expanded === b.name && (
                                        <motion.div
                                            initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
                                            className="overflow-hidden border-t border-white/5"
                                        >
                                            <div className="px-5 py-3 flex flex-wrap gap-1.5">
                                                {(b.collections || []).map(c => <ColBadge key={c} name={c} />)}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Restore modal */}
            <AnimatePresence>
                {restoring && (
                    <RestoreModal
                        backup={restoring}
                        onClose={() => setRestoring(null)}
                        onConfirm={handleRestore}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
